<template>
  <div
    class="icon-details-preview card compact with-border padding-top-0 with-border-radius display-flex flex-column flex-items-stretch flex-content-between"
    style="height: 100%; --card-margin-bottom: 0"
    :style="`${
      previewMode == 'night'
        ? '--border-color: var(--fa-navy); --card-background: var(--fa-navy); --balloon-color: var(--fa-dk-navy); --card-color: var(--white);'
        : '--border-color: var(--fa-lt-gravy);'
    }`"
  >
    <div
      class="padding-y-5xl display-flex flex-column flex-items-center flex-content-center positon-relative z-1"
      style="min-height: var(--icon-details-preview-min-height); flex-grow: 1; flex-shrink: 0; flex-basis: auto"
    >
      <div
        class="icon-details-preview-rendering position-absolute display-flex flex-column flex-items-center flex-content-center"
        style="inset: 0; overflow: hidden"
      >
        <span v-if="!isCustomIcon">
          <i
            :class="[`${translatedStyle} fa-${icon.name}${animation}${size}${rotation}`]"
            :style="`${hasCustomRotation ? customRotation : ''} ${
              isDuotone ? colorPrimary + colorSecondary + opacityPrimary + opacitySecondary : colorPrimary
            }`"
            style="line-height: 1"
          />
        </span>
        <span
          v-if="isCustomIcon && icon"
          :class="`${animation}${size}${rotation}`"
          :style="`${hasCustomRotation ? customRotation : ''} ${isDuotone ? colorPrimary + colorSecondary + opacityPrimary + opacitySecondary : colorPrimary}`"
        >
          <font-awesome-icon
            :icon="iconDefinition"
            class="uploaded-icon-preview"
            style="line-height: 1"
          />
        </span>
      </div>
    </div>

    <div
      v-if="displayIconPreviewControls"
      class="icon-details-preview-rendering-controls position-absolute z-2"
      style="bottom: var(--spacing-4xl); left: var(--card-horizontal-padding); right: var(--card-horizontal-padding)"
    >
      <div
        class="display-flex flex-row flex-nowrap flex-items-center flex-content-between position-absolute"
        style="--button-margin-bottom: 0; gap: var(--spacing-xl); width: 100%"
      >
        <div style="flex-grow: 1; text-align: left">
          <button
            :class="styleTabButtonClass"
            :style="toggleButtonStyle"
            aria-label="Show Styling Tools"
            data-balloon-pos="up"
            @click="toggleStyleOptions"
          >
            <font-awesome-icon
              :icon="$v7.solid.faPalette"
              size="lg"
            />
          </button>
          <span
            v-if="hasRotation"
            :class="styleTabButtonClass"
            :style="toggleButtonStyle"
            style="color: var(--fa-gravy); cursor: not-allowed; vertical-align: middle"
            aria-label="Can't animate while rotating or flipping"
            data-balloon-pos="up"
          >
            <font-awesome-icon
              :icon="$v7.solid.faFilm"
              size="lg"
            />
          </span>
          <button
            v-else
            :class="styleTabButtonClass"
            :style="toggleButtonStyle"
            aria-label="Show animation options"
            data-balloon-pos="up"
            @click="toggleAnimationOptions"
          >
            <font-awesome-icon
              :icon="$v7.solid.faFilm"
              size="lg"
            />
          </button>

          <button
            v-if="hasCustomStyling"
            :class="toggleButtonClass"
            :style="toggleButtonStyle"
            style="margin-left: var(--spacing-md)"
            aria-label="Reset Styling"
            data-balloon-pos="up"
            @click="resetStyles"
          >
            Reset<span class="sr-only"> All Custom Styling</span>
          </button>
        </div>

        <button
          v-if="previewMode == 'day'"
          :class="toggleButtonClass"
          :style="toggleButtonStyle"
          aria-label="Nox!"
          data-balloon-pos="up"
          @click.prevent="togglePreviewMode('night')"
        >
          <font-awesome-icon
            :icon="$v7.solid.faMoon"
            size="lg"
          />
          <span class="sr-only">View Icon on Dark Background</span>
        </button>
        <button
          v-if="previewMode == 'night'"
          :class="toggleButtonClass"
          :style="toggleButtonStyle"
          aria-label="Lumos!"
          data-balloon-pos="up"
          @click.prevent="togglePreviewMode('day')"
        >
          <font-awesome-icon
            :icon="$v7.solid.faSunBright"
            size="lg"
          />
          <span class="sr-only">View Icon on Light Background</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import isEqual from 'lodash/isEqual'
  import { toIconDefinition } from 'app/views/kits/utils'
  import { getShortCode } from 'app/releases'

  export default {
    name: 'IconPreview',

    props: {
      defaultStyles: {
        type: Object
      },
      icon: {
        type: Object
      },
      isCustomIcon: {
        type: Boolean
      },
      isDuotone: {
        type: Boolean
      },
      previewMode: {
        type: String
      },
      styling: {
        type: Object
      },
      displayIconPreviewControls: {
        type: Boolean,
        required: true
      },
      majorVersion: {
        type: Number,
        required: true
      }
    },

    data() {
      return {
        toggleButtonClass: 'link flat padding-y-3xs padding-x-0',
        styleTabButtonClass: 'link flat padding-sm display-inline-block'
      }
    },

    computed: {
      hasCustomRotation() {
        return Number.isInteger(parseInt(this.styling?.rotation))
      },

      hasRotation() {
        const rotate = this.styling.rotation
        if (rotate == null) {
          return false
        } else {
          return true
        }
      },

      customRotation() {
        return `--fa-rotate-angle: ${this.styling.rotation}deg;`
      },

      hasCustomStyling() {
        return !isEqual(this.defaultStyles, this.styling)
      },

      iconDefinition() {
        return toIconDefinition(this.icon)
      },

      translatedStyle() {
        return getShortCode(this.styling.family, this.styling.family === 'brands' ? '' : this.styling.style)
      },

      animation() {
        if (this.styling.animation === 'spin-reverse') {
          return ` fa-spin fa-spin-reverse`
        }

        if (this.styling.animation) {
          return ` fa-${this.styling.animation}`
        }

        return ''
      },

      colorPrimary() {
        if (this.styling.colorPrimary === this.defaultStyles.colorPrimary && this.isDuotone && this.previewMode === 'night') {
          return `--fa-primary-color: var(--white);`
        } else if (this.styling.colorPrimary && this.isDuotone) {
          return `--fa-primary-color: ${this.styling.colorPrimary};`
        } else if (this.styling.colorPrimary === this.defaultStyles.colorPrimary && this.previewMode === 'night') {
          return 'color: var(--white);'
        } else {
          return `color: ${this.styling.colorPrimary};`
        }
      },

      colorSecondary() {
        if (this.styling.colorSecondary === this.defaultStyles.colorSecondary && this.previewMode === 'night') {
          return `--fa-secondary-color: var(--white);`
        } else {
          return `--fa-secondary-color: ${this.styling.colorSecondary};`
        }
      },

      opacityPrimary() {
        if (this.styling.opacityPrimary) {
          return `--fa-primary-opacity: ${this.styling.opacityPrimary};`
        }

        return `--fa-primary-opacity: 1;`
      },

      opacitySecondary() {
        if (this.styling.opacitySecondary) {
          return `--fa-secondary-opacity: ${this.styling.opacitySecondary};`
        }

        return `--fa-secondary-opacity: 0.4;`
      },

      rotation() {
        if (this.styling.rotation) {
          if (this.hasCustomRotation) {
            return ` fa-rotate-by`
          } else {
            return ` fa-${this.styling.rotation}`
          }
        }

        return ''
      },

      size() {
        if (this.styling.size) {
          return ` fa-${this.styling.size}`
        }

        return ''
      },
      toggleButtonStyle() {
        let style = {}

        if (this.previewMode === 'day') {
          style['--button-color'] = 'var(--fa-dk-blue)'
          style['--button-hover-color'] = 'var(--fa-blue)'
        } else {
          style['--button-color'] = 'var(--fa-yellow)'
          style['--button-hover-color'] = 'var(--fa-dk-yellow)'
          style['--balloon-color'] = 'var(--fa-dk-blue)'
        }

        return {
          ...style,
          ...{
            '--button-background': 'transparent',
            '--button-hover-background': 'transparent',
            '--button-active-background': 'transparent',
            '--focus-outline': 'none'
          }
        }
      }
    },

    methods: {
      resetStyles() {
        this.$posthog.capture('icon_preview_reset_styles_button_click')
        return this.$emit('resetStyles')
      },

      toggleStyleOptions() {
        this.$posthog.capture('icon_preview_toggle_styling_click')
        this.$emit('toggleStyleOptions')
      },

      toggleAnimationOptions() {
        this.$posthog.capture('icon_preview_toggle_animations_click')
        this.$emit('toggleAnimationOptions')
      },

      togglePreviewMode(mode) {
        this.$posthog.capture('icon_preview_mode_button_click', { mode: mode })
        this.$emit('changePreviewMode', mode)
      }
    }
  }
</script>
